// import { isUndefinedOrNull } from '@flatbiz/utils';
import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { CSSProperties, FC, memo, useMemo, useRef } from 'react';
import { Grid, GridItem } from '../grid';
import { NativeProps } from '../utils/native-props';
import './styles/view.less';
import { KeyboardKeyItem } from './types';
import { getKeyboardKeyList } from './utils';

export type KeyboardProps = {
  type?: 'pure_number' | 'number' | 'idcard';
  customKey?: '-' | '.' | 'X' | null;
  customKeyboardKeyList?: KeyboardKeyItem[];
  showOkButton?: boolean;
  randomOrder?: boolean;
  gridColumns?: number;
  onConfirm?: () => void;
  onBackSpace?: () => void;
  onInput: (key: string) => void;
} & NativeProps;

/**
 * 自定义键盘【可自定义键值】
 * @param props
 * @returns
 *```
 * 1. customKey：默认键盘自定义键格
 * 2. 未设置customKeyboardKeyList，gridColumns属性无效
 * 3. 当设置customKeyboardKeyList后，customKey、showOkButton、randomOrder配置将失效
 * 4. 当设置type属性后，customKey失效
 *```
 */
const KeyboardInner: FC<KeyboardProps> = (props) => {
  const timeoutRef = useRef(-1);
  const intervalRef = useRef(-1);
  const customKey = useMemo(() => {
    if (props.type && props.type === 'pure_number') {
      return null;
    }
    if (props.type && props.type === 'idcard') {
      return 'X';
    }
    if (props.type && props.type === 'number') {
      return '.';
    }
    return props.customKey;
  }, [props.customKey, props.type]);

  const onBackSpace = hooks.useCallbackRef(() => {
    props.onBackSpace?.();
  });

  const onMouseUp = hooks.useCallbackRef((key: string) => {
    // event.preventDefault();
    if (!key || key === 'empty-custom') return;
    if (key === 'back') {
      props.onBackSpace?.();
      return;
    } else if (key === 'ok') {
      props.onConfirm?.();
      return;
    } else if (key === 'custom') {
      props.onInput?.(customKey || '');
      return;
    }
    props.onInput?.(key);
  });
  const onTouchStart = hooks.useCallbackRef((key: string) => {
    // event.preventDefault();
    if (key === 'back') {
      timeoutRef.current = window.setTimeout(() => {
        onBackSpace();
        intervalRef.current = window.setInterval(onBackSpace, 100);
      }, 700);
    }
  });
  const onTouchEnd = hooks.useCallbackRef(() => {
    // event.preventDefault();
    clearTimeout(timeoutRef.current);
    clearInterval(intervalRef.current);
  });

  const keyboardKeyList = useMemo(() => {
    if (props.customKeyboardKeyList) {
      return props.customKeyboardKeyList;
    }
    return getKeyboardKeyList({
      showOkButton: props.showOkButton,
      randomOrder: props.randomOrder,
      customKey: customKey,
    });
  }, [customKey, props.randomOrder, props.showOkButton, props.customKeyboardKeyList]);

  const columns = props.gridColumns && props.gridColumns > 0 ? props.gridColumns : props.showOkButton ? 4 : 3;
  return (
    <div
      className={classNames('v-keyboard', props.className)}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      style={props.style}
    >
      <Grid columns={columns} className="v-keyboard-grid" gap={0}>
        {keyboardKeyList.map((item, index) => {
          let keyInfo = {} as { key: string; name: string };
          const style = {} as CSSProperties;
          let className = '';
          if (typeof item !== 'string') {
            if (item.xSpan && item.xSpan > 0) {
              style.gridColumn = `span ${item.xSpan}`;
            }
            if (item.ySpan && item.ySpan > 0) {
              style.gridRow = `span ${item.ySpan}`;
            }
            className = `v-keyboard-grid-item-${item.key}`;
            keyInfo = { key: item.key, name: item.name };
          } else {
            keyInfo = { key: item, name: item };
            // className = 'v-keyboard-grid-item-key';
          }
          return (
            <GridItem
              key={index}
              style={style}
              className={className}
              onMouseUp={onMouseUp.bind(null, keyInfo.key)}
              onTouchStart={onTouchStart.bind(null, keyInfo.key)}
              onTouchEnd={onTouchEnd}
            >
              {keyInfo.name}
            </GridItem>
          );
        })}
      </Grid>
    </div>
  );
};

KeyboardInner.defaultProps = {
  showOkButton: true,
  customKey: '.',
  randomOrder: false,
};

export const Keyboard = memo(KeyboardInner, () => {
  return true;
});
